/**
 * WizardStepDetails (Step 5)
 *
 * Conditional requirements and additional details:
 * - Estimated project value (triggers NOC check)
 * - Owner-builder status
 * - Client information
 * - Commercial metadata (if commercial property)
 */

import { useEffect, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertTriangle, DollarSign, User, Building2, Sparkles } from 'lucide-react';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { useJurisdictionDetail } from '@/hooks/useJurisdictions';
import { getNocThreshold } from '@/lib/notice-of-commencement';
import type {
  WizardDetailsData,
  WizardBasicsData,
  WizardPermitsData,
  ConditionalRequirement,
} from '@/types/wizard';

interface WizardStepDetailsProps {
  data: WizardDetailsData;
  onUpdate: (data: Partial<WizardDetailsData>) => void;
  basicsData: WizardBasicsData;
  permitsData: WizardPermitsData;
}

export function WizardStepDetails({
  data,
  onUpdate,
  basicsData,
  permitsData,
}: WizardStepDetailsProps) {
  const isCommercial = basicsData.propertyType === 'commercial';
  const { data: jurisdictionDetail } = useJurisdictionDetail(basicsData.jurisdictionId ?? null);

  const nocThreshold = useMemo(() => {
    const acceptedPermitIds = new Set(permitsData.acceptedPermitIds);
    const acceptedRecommendations = permitsData.recommendations.filter((rec) =>
      acceptedPermitIds.has(rec.permitTypeId) || rec.userAccepted || rec.required
    );
    const permitHints = [
      ...acceptedRecommendations.map((rec) => rec.permitCode),
      ...acceptedRecommendations.map((rec) => rec.tradeCode || ''),
    ].filter(Boolean);

    return getNocThreshold(jurisdictionDetail?.notice_of_commencement, permitHints);
  }, [jurisdictionDetail?.notice_of_commencement, permitsData.acceptedPermitIds, permitsData.recommendations]);

  const nocThresholdAmount = nocThreshold?.amount ?? 2500;

  // Calculate conditional requirements based on project value and other factors
  const conditionalRequirements = useMemo(() => {
    const requirements: ConditionalRequirement[] = [];

    // NOC check
    if (data.estimatedValue && data.estimatedValue >= nocThresholdAmount) {
      requirements.push({
        id: 'noc',
        type: 'noc',
        name: 'Notice of Commencement (NOC)',
        description: `Required for projects valued at $${nocThresholdAmount.toLocaleString()} or more`,
        triggered: true,
        triggerReason: `Project value ($${data.estimatedValue?.toLocaleString()}) exceeds threshold`,
        userAcknowledged: data.conditionalRequirements.find((r) => r.id === 'noc')?.userAcknowledged,
      });
    }

    // Owner-builder check
    if (data.isOwnerBuilder) {
      requirements.push({
        id: 'owner_builder',
        type: 'owner_builder',
        name: 'Owner-Builder Disclosure',
        description: 'Special requirements apply when property owner pulls the permit',
        triggered: true,
        triggerReason: 'Owner-builder status indicated',
        userAcknowledged: data.conditionalRequirements.find((r) => r.id === 'owner_builder')?.userAcknowledged,
      });
    }

    // HVHZ check
    if (basicsData.isHvhz) {
      requirements.push({
        id: 'hvhz',
        type: 'hvhz',
        name: 'High Velocity Hurricane Zone Requirements',
        description: 'Additional product approvals and documentation required',
        triggered: true,
        triggerReason: 'Property located in HVHZ',
        userAcknowledged: data.conditionalRequirements.find((r) => r.id === 'hvhz')?.userAcknowledged,
      });
    }

    return requirements;
  }, [data.estimatedValue, data.isOwnerBuilder, basicsData.isHvhz, data.conditionalRequirements, nocThresholdAmount]);

  // Update conditional requirements when they change
  useEffect(() => {
    if (conditionalRequirements.length > 0) {
      onUpdate({ conditionalRequirements });
    }
  }, [conditionalRequirements, onUpdate]);

  // Handle value change with proper formatting
  const handleValueChange = (value: string) => {
    const numValue = parseInt(value.replace(/[^0-9]/g, ''), 10);
    onUpdate({ estimatedValue: isNaN(numValue) ? undefined : numValue });
  };

  return (
    <div className="space-y-8">
      {/* Project Value */}
      <div className="space-y-2">
        <Label htmlFor="estimatedValue" className="flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Estimated Project Value
        </Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            $
          </span>
          <Input
            id="estimatedValue"
            type="text"
            placeholder="0"
            className="pl-7"
            value={data.estimatedValue?.toLocaleString() || ''}
            onChange={(e) => handleValueChange(e.target.value)}
          />
        </div>
        <p className="text-sm text-muted-foreground">
          Used to determine NOC requirements. Projects over ${nocThresholdAmount.toLocaleString()} require a Notice of Commencement.
        </p>
      </div>

      {/* Owner-Builder */}
      <div className="space-y-3">
        <Label>Is this an owner-builder project?</Label>
        <RadioGroup
          value={data.isOwnerBuilder === true ? 'yes' : data.isOwnerBuilder === false ? 'no' : ''}
          onValueChange={(value) => onUpdate({ isOwnerBuilder: value === 'yes' })}
          className="flex gap-4"
        >
          <Label
            htmlFor="owner-no"
            className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 flex-1 [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
          >
            <RadioGroupItem value="no" id="owner-no" />
            <span>No, licensed contractor</span>
          </Label>
          <Label
            htmlFor="owner-yes"
            className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 flex-1 [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
          >
            <RadioGroupItem value="yes" id="owner-yes" />
            <span>Yes, owner-builder</span>
          </Label>
        </RadioGroup>
        <p className="text-sm text-muted-foreground">
          Owner-builder permits have special requirements and limitations.
        </p>
      </div>

      {/* Conditional Requirements Alert */}
      {conditionalRequirements.filter((r) => r.triggered).length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              Additional Requirements Detected
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {conditionalRequirements
              .filter((r) => r.triggered)
              .map((req) => (
                <div key={req.id} className="flex items-start gap-3 p-3 bg-white dark:bg-background rounded-lg">
                  <Badge variant="outline" className="shrink-0 mt-0.5">
                    {req.type.toUpperCase()}
                  </Badge>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{req.name}</p>
                      <ConfidenceBadge
                        level="high"
                        source={req.triggerReason}
                        showLabel={false}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">{req.description}</p>
                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-1 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      Auto-detected: {req.triggerReason}
                    </p>
                  </div>
                </div>
              ))}
          </CardContent>
        </Card>
      )}

      {/* Client Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <User className="h-5 w-5" />
            Client Information (Optional)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="clientName" className="text-sm text-muted-foreground">
              Client Name
            </Label>
            <Input
              id="clientName"
              placeholder="John Smith"
              value={data.client?.name || ''}
              onChange={(e) =>
                onUpdate({ client: { ...data.client, name: e.target.value } })
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="clientEmail" className="text-sm text-muted-foreground">
                Email
              </Label>
              <Input
                id="clientEmail"
                type="email"
                placeholder="john@example.com"
                value={data.client?.email || ''}
                onChange={(e) =>
                  onUpdate({ client: { ...data.client, email: e.target.value } })
                }
              />
            </div>
            <div>
              <Label htmlFor="clientPhone" className="text-sm text-muted-foreground">
                Phone
              </Label>
              <Input
                id="clientPhone"
                placeholder="(555) 123-4567"
                value={data.client?.phone || ''}
                onChange={(e) =>
                  onUpdate({ client: { ...data.client, phone: e.target.value } })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Commercial Metadata */}
      {isCommercial && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Commercial Project Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="buildingType" className="text-sm text-muted-foreground">
                  Building Type
                </Label>
                <Input
                  id="buildingType"
                  placeholder="Office, Retail, Restaurant..."
                  value={data.commercialMetadata?.buildingType || ''}
                  onChange={(e) =>
                    onUpdate({
                      commercialMetadata: {
                        ...data.commercialMetadata,
                        buildingType: e.target.value,
                      },
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="occupancyType" className="text-sm text-muted-foreground">
                  Occupancy Type
                </Label>
                <Input
                  id="occupancyType"
                  placeholder="A-2, B, M..."
                  value={data.commercialMetadata?.occupancyType || ''}
                  onChange={(e) =>
                    onUpdate({
                      commercialMetadata: {
                        ...data.commercialMetadata,
                        occupancyType: e.target.value,
                      },
                    })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="squareFootage" className="text-sm text-muted-foreground">
                  Square Footage
                </Label>
                <Input
                  id="squareFootage"
                  type="number"
                  placeholder="5000"
                  value={data.commercialMetadata?.squareFootage || ''}
                  onChange={(e) =>
                    onUpdate({
                      commercialMetadata: {
                        ...data.commercialMetadata,
                        squareFootage: parseInt(e.target.value, 10) || undefined,
                      },
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="numberOfStories" className="text-sm text-muted-foreground">
                  Number of Stories
                </Label>
                <Input
                  id="numberOfStories"
                  type="number"
                  placeholder="1"
                  value={data.commercialMetadata?.numberOfStories || ''}
                  onChange={(e) =>
                    onUpdate({
                      commercialMetadata: {
                        ...data.commercialMetadata,
                        numberOfStories: parseInt(e.target.value, 10) || undefined,
                      },
                    })
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
